"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import ReactMarkdown from "react-markdown";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { ExternalLink, X } from "lucide-react";
import { exitFullscreen } from "@/lib/fullscreen";

export interface PromptData {
  id: string;
  title: string;
  content: string;
  imageUrl?: string | null;
  ctaText?: string | null;
  ctaLink?: string | null;
}

interface PromptPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  huntId?: string;
  onClose?: () => void;
}

export function PromptPopup({ open, onOpenChange, huntId, onClose }: PromptPopupProps) {
  const [prompt, setPrompt] = useState<PromptData | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const wasOpen = useRef(false);

  const handleClose = useCallback(() => {
    void exitFullscreen();
    setDrawerOpen(false);
    onOpenChange(false);
    onClose?.();
  }, [onOpenChange, onClose]);

  useEffect(() => {
    if (open && !wasOpen.current) {
      let cancelled = false;
      const params = new URLSearchParams();
      if (huntId) params.set("huntId", huntId);

      fetch(`/api/prompts/random?${params.toString()}`)
        .then((res) => {
          if (!res.ok) console.error("Failed to fetch prompt:", res.status);
          return res.ok ? res.json() : { prompt: null };
        })
        .then((data) => {
          if (cancelled) return;
          if (data.prompt) {
            setPrompt(data.prompt);
            setDrawerOpen(true);
          } else {
            onOpenChange(false);
            onClose?.();
          }
        })
        .catch(() => {
          if (!cancelled) {
            onOpenChange(false);
            onClose?.();
          }
        });

      return () => { cancelled = true; };
    }

    if (!open) {
      void exitFullscreen();
      setPrompt(null);
      setDrawerOpen(false);
    }

    wasOpen.current = open;
  }, [open, huntId, onOpenChange, onClose]);

  const handleCtaClick = () => {
    if (prompt?.ctaLink) {
      window.open(prompt.ctaLink, "_blank", "noopener,noreferrer");
    }
    handleClose();
  };

  return (
    <Drawer open={drawerOpen} onOpenChange={(newOpen) => {
      if (!newOpen) handleClose();
    }}>
      <DrawerContent
        className="!inset-0 !top-0 !mt-0 !h-dvh !max-h-dvh !w-full !rounded-none"
        showHandle={false}
        aria-describedby={undefined}
      >
        <DrawerHeader className="border-b px-4 pb-3 pt-drawer-safe-top">
          <div className="flex items-start gap-2">
            <div className="w-10 shrink-0" aria-hidden />
            <DrawerTitle className="min-w-0 flex-1 text-center text-xl text-balance">
              {prompt?.title}
            </DrawerTitle>
            <div className="flex w-10 shrink-0 items-start justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-full p-2 hover:bg-muted active:bg-muted"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </button>
            </div>
          </div>
          <DrawerDescription className="sr-only">
            Marketing prompt content
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto overscroll-contain bg-[hsl(210,40%,98%)] p-6">
          {prompt && (
            <div className="flex flex-col gap-4 p-4">
              {prompt.imageUrl && (
                <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-muted">
                  <Image
                    src={prompt.imageUrl}
                    alt=""
                    fill
                    className="object-contain"
                    sizes="(max-width: 768px) 100vw, 768px"
                  />
                </div>
              )}
              <div className="prose prose-base max-w-none dark:prose-invert prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5">
                <ReactMarkdown>{prompt.content}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>

        {prompt && (
          <DrawerFooter className="border-t">
            {prompt.ctaText && prompt.ctaLink ? (
              <div className="flex flex-col gap-2">
                <Button
                  onClick={handleCtaClick}
                  className="h-12 w-full rounded-xl text-base font-medium"
                >
                  {prompt.ctaText}
                  <ExternalLink className="ml-2 size-4" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleClose}
                  className="h-10 w-full text-muted-foreground"
                >
                  Continue
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleClose}
                className="h-12 w-full rounded-xl text-base font-medium"
              >
                Continue
              </Button>
            )}
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
}
